import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import ACTION from "../../store/action";
import { selectFiltersModified } from "../../store/selector";
import { selectedViews } from "../../constant/table";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { useInlineEditTracking } from "../../component/TableSection/InlineEdit/useInlineEditTracking";
import { InlineEditStatus } from "../../utils/pendo/useStandardizedTracking";
import { updateEnrollmentInlineAPI } from "../../api/api";
import { DATE_DATA_FORMAT } from "../../utils/date";
import { APPLICATIONS } from "../../constant/applications";
import { PROGRAM_TYPES } from "@tailormed/common-client/constant/assistanceProgram";
import { APPLICATION_RENEWAL_TIME_TYPES } from "../../constant/applicationRenewalTimeTypes";
import { getValidThroughDate } from "../../component/Patient/PatientApplications/PatientApplicationDetails/ApprovedFields";
import { isMaxBenefitRequiredForRecord } from "../../utils/programSubtypes";
import { toAssigneeIdArray } from "../../utils/formaters";

// Statuses for which Valid Through is mandatory/auto-populated (matches the track tab).
const APPROVED_LIKE_STATUSES = [APPLICATIONS.statuses.approved, APPLICATIONS.statuses.temporarilyApproved];

// newTrackTab parity: fields the target status requires (only the ones the table row carries).
// Changing status from the list shouldn't leave any of these empty (the track form enforces them).
const REQUIRED_FIELDS_BY_STATUS = {
  [APPLICATIONS.statuses.prepare]: ["assignee"],
  [APPLICATIONS.statuses.applied]: ["assignee"],
  [APPLICATIONS.statuses.appliedToWaitlist]: ["assignee"],
  [APPLICATIONS.statuses.approved]: ["assignee", "approvedDate", "approvedAmount", "validThrough"],
  [APPLICATIONS.statuses.temporarilyApproved]: ["assignee", "approvedDate", "approvedAmount", "validThrough"]
};

// `plan` carries the auto-fills (validThrough / effectiveIndefinitely) computed for this commit,
// so Valid Through counts as present when the plan will set it even if the record is still empty.
const getMissingRequiredFields = (status, record, plan = {}) =>
  (REQUIRED_FIELDS_BY_STATUS[status] || []).filter((field) => {
    if (field === "assignee") return toAssigneeIdArray(record.enrollmentAssignee).length === 0;
    if (field === "approvedDate") return !record.approvedDate;
    // Max Benefit isn't required for free-drug programs.
    if (field === "approvedAmount") return isMaxBenefitRequiredForRecord(record) && !record.approvedAmount;
    // Valid Through is required unless the app is marked indefinite (track tab). Honors the plan's
    // auto-fill — only missing when no date/indefinite can be derived (e.g. no renewal definition).
    if (field === "validThrough") {
      const indefinite = plan?.patch?.effectiveIndefinitely || record.effectiveIndefinitely;
      const validThrough = plan?.patch?.approvalValidThrough || record.approvalValidThrough;
      return !indefinite && !validThrough;
    }
    return false;
  });

// Statuses that stamp Applied Date with today if unset — mirrors EnrollmentBulkHandler
// (the inline PATCH path doesn't auto-fill it, so we send it explicitly).
const APPLIED_DATE_STATUSES = [
  APPLICATIONS.statuses.applied,
  APPLICATIONS.statuses.appliedToWaitlist,
  APPLICATIONS.statuses.missingInformation,
  APPLICATIONS.statuses.canceled,
  APPLICATIONS.statuses.rejected,
  APPLICATIONS.statuses.temporarilyApproved,
  APPLICATIONS.statuses.approved
];

// MVP-30981 — statuses the backend's Funds Management guard accepts for an internal FM enrollment
// (getFundsManagementStatusError). When the target status is any other, the request is rejected with
// the FM-specific message regardless of the row's fields, so the required-fields pre-check must not
// run first — it would replace that message with the generic "required fields" toast, unlike every
// other forbidden status (and bulk actions), which surface the backend's message.
const FUNDS_MANAGEMENT_EDITABLE_STATUSES = [APPLICATIONS.statuses.prepare, APPLICATIONS.statuses.applied];
const isBlockedByFundsManagementGuard = (record, status) =>
  Boolean(record.isFundsManagementProgram) &&
  record.programType === PROGRAM_TYPES.Internal &&
  !FUNDS_MANAGEMENT_EDITABLE_STATUSES.includes(status);

const isValidThroughInPast = (validThrough) => !!validThrough && moment(validThrough).isBefore(moment(), "day");

const MYSQL_DATETIME_FORMAT = `${DATE_DATA_FORMAT} HH:mm:ss`;
const toMySqlDateTime = (value) => {
  if (value == null) return null;
  if (!moment.isMoment(value)) return value;
  return value.clone().startOf("day").add(12, "hours").utc().format(MYSQL_DATETIME_FORMAT);
};

const buildPlanForEnrollment = (record, columnKey, newValue) => {
  switch (columnKey) {
    case "applicationStatus": {
      const fields = { status: newValue };
      const patch = { applicationStatus: newValue };
      // Track-tab parity: an applied-or-later status stamps Applied Date with today if unset.
      if (APPLIED_DATE_STATUSES.includes(newValue) && !record.appliedDate) {
        const today = toMySqlDateTime(moment());
        fields.appliedDate = today;
        patch.appliedDate = today;
      }
      // Track-tab parity: an approved-like status auto-populates Valid Through from the
      // program's renewal config, when not already set / indefinite.
      if (APPROVED_LIKE_STATUSES.includes(newValue) && !record.effectiveIndefinitely && !record.approvalValidThrough) {
        const validThrough = getValidThroughDate(
          {
            applicationRenewalTimeType: record.applicationRenewalTimeType,
            applicationRenewalTimeInMonths: record.applicationRenewalTimeInMonths
          },
          moment(),
          false
        );
        if (validThrough) {
          fields.validThrough = toMySqlDateTime(validThrough);
          patch.approvalValidThrough = toMySqlDateTime(validThrough);
        } else if (record.applicationRenewalTimeType === APPLICATION_RENEWAL_TIME_TYPES.never) {
          // Track-tab parity: a never-renews program has no expiry → mark it valid indefinitely
          // instead of leaving Valid Through empty.
          fields.effectiveIndefinitely = true;
          patch.effectiveIndefinitely = true;
        }
      }
      return { fields, patch };
    }
    case "validThrough": {
      const { validThrough, effectiveIndefinitely } = newValue || {};
      const patch = {
        approvalValidThrough: toMySqlDateTime(validThrough),
        effectiveIndefinitely: Boolean(effectiveIndefinitely)
      };
      // Parity with the backend: an approved app whose Valid Through is past is Expired.
      if (
        record.applicationStatus === APPLICATIONS.statuses.approved &&
        !effectiveIndefinitely &&
        isValidThroughInPast(validThrough)
      ) {
        patch.applicationStatus = APPLICATIONS.statuses.expired;
      }
      return {
        fields: { validThrough: toMySqlDateTime(validThrough), effectiveIndefinitely: Boolean(effectiveIndefinitely) },
        patch
      };
    }
    case "approvedAmount": {
      const num = newValue === "" || newValue == null ? null : Number(newValue);
      const patch = { approvedAmount: num };
      // Mirror the backend balance formula (fundBalance = approvedAmount − used) optimistically,
      // since the PATCH returns only the id. Only when the row carries a balance — programs that
      // don't track claims have fundBalance = null, so don't fabricate one.
      const prevApproved = record.approvedAmount != null ? Number(record.approvedAmount) : null;
      const prevBalance = record.fundBalance != null ? Number(record.fundBalance) : null;
      if (prevBalance != null && prevApproved != null) {
        const used = prevApproved - prevBalance;
        patch.fundBalance = num != null ? num - used : null;
      }
      // else: leave fundBalance untouched — refetch returns the canonical value.
      return {
        fields: { approvedAmount: num },
        patch
      };
    }
    case "appliedDate": {
      const date = toMySqlDateTime(newValue);
      return {
        fields: { appliedDate: date },
        patch: { appliedDate: date }
      };
    }
    case "approvedDate": {
      // Editor may commit `{ approvedDate, validThrough?, effectiveIndefinitely? }` together
      // when the user toggles "valid indefinitely" — mirrors the track-tab behavior.
      const { approvedDate, validThrough, effectiveIndefinitely } =
        newValue && typeof newValue === "object" && !moment.isMoment(newValue) ? newValue : { approvedDate: newValue };

      const fields = { approvedDate: toMySqlDateTime(approvedDate) };
      const patch = { approvedDate: toMySqlDateTime(approvedDate) };

      if (validThrough !== undefined) {
        fields.validThrough = toMySqlDateTime(validThrough);
        patch.approvalValidThrough = toMySqlDateTime(validThrough);
      }
      if (effectiveIndefinitely !== undefined) {
        fields.effectiveIndefinitely = Boolean(effectiveIndefinitely);
        patch.effectiveIndefinitely = Boolean(effectiveIndefinitely);
      }
      return { fields, patch };
    }
    case "enrollmentAssignee": {
      // Multi-assignee (track-tab parity): commit the selected ids as an array. The backend
      // replaces the enrollment's junction rows; the column resolves ids → names from the store,
      // so the id array alone is enough for the optimistic patch.
      const ids = toAssigneeIdArray(newValue);
      return {
        fields: { assignees: ids },
        patch: { enrollmentAssignee: ids }
      };
    }
    default:
      return null;
  }
};

// `options.view` selects which Redux slice receives the optimistic patch (allApplications, pendingRenewal).
const useInlineEditEnrollment = (lookups = {}, options = {}) => {
  const { view = selectedViews.allApplications } = options;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filtersModified = useSelector(selectFiltersModified);
  const { trackInlineEditTriggered, trackInlineEditCompleted } = useInlineEditTracking();

  const commit = useCallback(
    async (record, columnKey, newValue) => {
      const plan = buildPlanForEnrollment(record, columnKey, newValue);
      if (!plan) return { ok: false };

      // newTrackTab parity: don't let a status change leave the target status's required fields
      // empty (e.g. approving without Max Benefit / assignee) — block and prompt to fill them.
      // Skipped when the FM guard will reject the status anyway, so the backend's 400 message is
      // what the user sees (same toast as any other forbidden status on an internal FM enrollment).
      if (
        columnKey === "applicationStatus" &&
        !isBlockedByFundsManagementGuard(record, newValue) &&
        getMissingRequiredFields(newValue, record, plan).length > 0
      ) {
        ErrorMessage(t("inline_edit.required_fields_for_status"));
        return { ok: false };
      }

      const { patientId, papId, applicationId } = record;
      if (!patientId || !papId || !applicationId) {
        ErrorMessage(t("inline_edit.error_toast"));
        return { ok: false };
      }

      trackInlineEditTriggered({ attribute: columnKey });
      const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      try {
        await updateEnrollmentInlineAPI(patientId, papId, applicationId, plan.fields);
        dispatch(ACTION.updateEnrollmentsInPlace(view, [{ ...record, ...plan.patch }]));
        // Active filters: the edited row may no longer match, so re-run the search (backend
        // applies filters). Skipped when no filter is set, to avoid a needless fetch.
        if (filtersModified) {
          dispatch(ACTION.setTableDataAction(null, view));
        }
        SuccessMessage(t("inline_edit.success_toast"));
        trackInlineEditCompleted({
          attribute: columnKey,
          status: InlineEditStatus.SUCCESS,
          runtimeMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt)
        });
        return { ok: true };
      } catch (error) {
        // A 400 from the inline update is an intentional edit guard (e.g. MVP-30981: an internal
        // Funds Management enrollment may only be set to Prepare/Applied) — surface the server's
        // message, matching how bulk actions reports the same rejection. Anything else stays generic.
        const guardMessage = error?.response?.status === 400 ? error?.response?.data?.errors?.[0]?.message : null;
        ErrorMessage(guardMessage || t("inline_edit.error_toast"));
        trackInlineEditCompleted({
          attribute: columnKey,
          status: InlineEditStatus.FAILURE,
          runtimeMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt)
        });
        return { ok: false, error };
      }
    },
    [dispatch, t, filtersModified, trackInlineEditTriggered, trackInlineEditCompleted, lookups, view]
  );

  return { commit };
};

export default useInlineEditEnrollment;
